import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Button, Tag, Typography, Space, Spin, Alert, Pagination } from 'antd';
import {
    ShopOutlined,
    SearchOutlined,
    BarChartOutlined,
    CommentOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetAdminBrandsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { IAdminBrandResponse } from '../../services/nestjs-base-api';

const { Title, Text } = Typography;
const { Search } = Input;

export const AdminBrandsPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [limit] = useState(12);
    const [search, setSearch] = useState('');

    const { data, isLoading, error } = useGetAdminBrandsQuery({
        jwtToken: user?.jwtToken || '',
        page,
        limit,
        search: search || undefined,
    });

    // CSS for mock data styling - must be at the top with other hooks
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .mock-brand-card:hover {
                background-color: #ffe7ba !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    const handleBrandEquityClick = (brand: IAdminBrandResponse) => {
        navigate(`/admin/brands/${brand.id}/brand-equity`);
    };

    const handleStatementSentimentClick = (brand: IAdminBrandResponse) => {
        navigate(`/admin/brands/${brand.id}/statements`);
    };

    const handleSearch = (value: string) => {
        setSearch(value);
        setPage(1); // Reset to first page when searching
    };

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading brands"
                    description="Unable to fetch admin brands data. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    const brands = data?.data || [];
    const total = data?.total || 0;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>
                    <ShopOutlined style={{ marginRight: 8 }} />
                    Brand Management
                </Title>
                <Text type="secondary">
                    {total} brand{total !== 1 ? 's' : ''} total
                </Text>
            </div>

            {/* Search */}
            <Card style={{ marginBottom: 24 }}>
                <Search
                    placeholder="Search brands by name or organization..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    onSearch={handleSearch}
                    style={{ maxWidth: 400 }}
                />
            </Card>

            {/* Brand Cards */}
            <Row gutter={[16, 16]}>
                {brands.map((brand) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={brand.id}>
                        <Card
                            hoverable
                            className={brand.isMock ? 'mock-brand-card' : ''}
                            style={{ 
                                height: '100%',
                                ...(brand.isMock ? {
                                    backgroundColor: '#fff7e6',
                                    borderLeft: '4px solid #fa8c16',
                                    borderColor: '#fa8c16'
                                } : {})
                            }}
                            styles={{ body: {display: 'flex', flexDirection: 'column', height: '100%'} }}
                        >
                            <div style={{ flex: 1 }}>
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8 }}>
                                        <Title level={4} style={{ margin: 0 }}>
                                            {brand.name}
                                        </Title>
                                        {brand.isMock && (
                                            <Tag color="orange" style={{fontSize: '10px'}}>
                                                MOCK DATA
                                            </Tag>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                        <ApartmentOutlined style={{ marginRight: 4, color: '#666' }} />
                                        <Text type="secondary">{brand.organisationName}</Text>
                                    </div>
                                    <Tag color={brand.status?.toLowerCase() === 'active' ? 'green' : 'red'}>
                                        {brand.status?.toUpperCase() || 'UNKNOWN'}
                                    </Tag>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Surveys:</Text>
                                            <Text strong>{brand.surveyCount}</Text>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Responses:</Text>
                                            <Text strong>{brand.responseCount}</Text>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Created:</Text>
                                            <Text>{new Date(brand.createdAt).toLocaleDateString()}</Text>
                                        </div>
                                    </Space>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 16 }}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Button
                                        type="primary"
                                        block
                                        icon={<BarChartOutlined />}
                                        onClick={() => handleBrandEquityClick(brand)}
                                        style={{ marginBottom: 8 }}
                                    >
                                        Brand Equity
                                    </Button>
                                    <Button
                                        block
                                        icon={<CommentOutlined />}
                                        onClick={() => handleStatementSentimentClick(brand)}
                                    >
                                        Statement Sentiment
                                    </Button>
                                </Space>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Empty State */}
            {brands.length === 0 && (
                <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <ShopOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                    <Title level={4} type="secondary">No brands found</Title>
                    <Text type="secondary">
                        {search ? 'Try adjusting your search criteria.' : 'No brands have been created yet.'}
                    </Text>
                </Card>
            )}

            {/* Pagination */}
            {total > limit && (
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Pagination
                        current={page}
                        total={total}
                        pageSize={limit}
                        onChange={setPage}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => 
                            `${range[0]}-${range[1]} of ${total} brands`
                        }
                    />
                </div>
            )}
        </div>
    );
};